import { useState, useCallback, useEffect } from 'react';
import { Col, Form, Input, Row } from 'antd';
import { useChangePassword } from '@/hooks';
import { Flex, AppButton } from '@/components';
import formErrors from '@/utils/formErrors';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getPasswordConfig } from '@/services/user.service';
import { storage } from '@/utils';
import { jwtDecode } from 'jwt-decode';
import { createPasswordValidator } from '@/utils/passwordValidator';

const gap = 24;

interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  requirements: string[];
  strength?: number;
}

export const ChangePassword = () => {
  const [submitted, setSubmitted] = useState(false);
  const { formik, loading } = useChangePassword();
  const [t] = useTranslation(['common']);
  const [validatePassword, setValidatePassword] = useState<((password: string) => PasswordValidationResult) | null>(null);
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidationResult | null>(null);

  const token = storage.get('TOKEN', null);
  const user = token ? jwtDecode(token.toString()) as any : null;

  const { data: passwordConfig } = useQuery({
    queryKey: ['users/password-configs'],
    queryFn: async () => (await getPasswordConfig(user?.user_name)),
    enabled: !!user?.user_name
  });

  useEffect(() => {
    if (passwordConfig?.payload?.[0]) {
      setValidatePassword(() => createPasswordValidator(passwordConfig.payload[0]));
    }
  }, [passwordConfig]);

  const validateForm = useCallback(() => {
    if (!validatePassword) return true;
    
    const validation = validatePassword(formik.values.newPassword || '');
    setPasswordValidation(validation);
    
    return validation.isValid;
  }, [validatePassword, formik.values.newPassword]);

  const handleSubmit = useCallback(() => {
    setSubmitted(true);
    const isValid = validateForm();
    if (isValid) {
      formik.submitForm();
    }
  }, [formik, validateForm]);

  const handlePasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(e);
    if (validatePassword) {
      const validation = validatePassword(e.target.value);
      setPasswordValidation(validation);
    }
  }, [formik, validatePassword]);

  const { values, errors } = formik;

  return (
    <Flex direction="column" className="w-full text-base" gap={gap}>
      <Row className="w-full" gutter={[gap, gap]}>
        <Col xl={6} lg={8} span={12}>
          <Form.Item {...formErrors(errors.currentPassword, submitted)}>
            <label className="required-label">{t('common:labels.current-password')}</label>
            <Input.Password 
              name="currentPassword" 
              value={values.currentPassword} 
              onChange={formik.handleChange} 
            />
          </Form.Item>
        </Col>
      </Row>
      <Row className="w-full" gutter={[gap, gap]}>
        <Col xl={6} lg={8} span={12}>
          <Form.Item
            {...formErrors(errors.newPassword, submitted)}
            help={
              submitted && passwordValidation?.errors && passwordValidation.errors.length > 0 ? (
                <div className="password-errors-list">
                  {passwordValidation.errors.map((error, index) => (
                    <div key={index} className="text-red-500 text-xs mt-1">
                      {error}
                    </div>
                  ))}
                </div>
              ) : null
            }
          >
            <label className="required-label">{t('common:labels.new-password')}</label>
            <Input.Password 
              name="newPassword" 
              value={values.newPassword} 
              onChange={handlePasswordChange} 
            />
          </Form.Item>
        </Col>
        <Col xl={6} lg={8} span={12}>
          <Form.Item {...formErrors(errors.newPasswordConfirm, submitted)}>
            <label className="required-label">{t('common:labels.confirm-new-password')}</label>
            <Input.Password 
              name="newPasswordConfirm" 
              value={values.newPasswordConfirm} 
              onChange={formik.handleChange} 
            />
          </Form.Item>
        </Col>
      </Row>
      <Row className="w-full mt-auto" gutter={[gap, gap]}>
        <Col span={4} className="!ms-auto">
          <AppButton
            appVariant="submit"
            className="w-full"
            loading={loading}
            onClick={handleSubmit}
          >
            {t('common:labels.update')}
          </AppButton>
        </Col>
      </Row>
    </Flex>
  );
};

export default ChangePassword;